"use client";

/**
 * Single Paddle.js loader for the whole web app.
 * - Sandbox vs live is decided by the client token's prefix (test_ = sandbox),
 *   so a sandbox token can never run against live or vice versa.
 * - Initialized once; Paddle Retain's pwCustomer is attached afterwards with
 *   Paddle.Update once the signed-in user's Paddle customer ID is known.
 * - The server-side API key never appears here.
 */

type PricePreviewLine = {
  price?: { id?: string };
  formattedTotals?: { subtotal?: string; discount?: string; total?: string };
  /** Lowest currency unit, as strings ("2200" = 22.00). */
  totals?: { subtotal?: string; discount?: string; tax?: string; total?: string };
};

/** What Paddle.js reports while a checkout is open (opened, completed, closed…). */
/** Totals in Paddle.js events are plain numbers in the currency (138.03), not cents. */
export type PaddleTotals = { subtotal?: number; discount?: number; tax?: number; total?: number; balance?: number };
export type PaddleEvent = {
  name?: string;
  data?: {
    transaction_id?: string;
    status?: string;
    currency_code?: string;
    totals?: PaddleTotals;
    recurring_totals?: PaddleTotals | null;
    items?: {
      price_name?: string | null;
      product?: { name?: string } | null;
      billing_cycle?: { interval?: string; frequency?: number } | null;
      totals?: PaddleTotals;
    }[];
  };
};

type PaddleGlobal = {
  Initialize: (config: {
    token: string;
    pwCustomer?: { id: string };
    eventCallback?: (event: PaddleEvent) => void;
  }) => void;
  Update?: (config: { pwCustomer?: { id: string } }) => void;
  Environment?: { set: (env: "sandbox" | "production") => void };
  Checkout: {
    open: (options: {
      transactionId: string;
      settings?: {
        displayMode?: "overlay" | "inline";
        variant?: "one-page" | "multi-page";
        successUrl?: string;
        theme?: "light" | "dark";
        // inline checkout: Paddle draws its payment form inside the element with this class
        frameTarget?: string;
        frameInitialHeight?: number;
        frameStyle?: string;
        allowLogout?: boolean;
        showAddDiscounts?: boolean;
      };
    }) => void;
    close?: () => void;
  };
  PricePreview?: (request: {
    items: { priceId: string; quantity: number }[];
    address?: { countryCode: string };
    discountId?: string;
  }) => Promise<{ data?: { currencyCode?: string; details?: { lineItems?: PricePreviewLine[] } } }>;
};

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

export const PADDLE_CLIENT_TOKEN =
  process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN ?? "";
export const paddleConfigured = Boolean(PADDLE_CLIENT_TOKEN);

const listeners = new Set<(event: PaddleEvent) => void>();

/** Hear what happens in an open checkout. Returns the function that stops listening. */
export function onPaddleEvent(fn: (event: PaddleEvent) => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function tell(event: PaddleEvent) {
  listeners.forEach((fn) => {
    try {
      fn(event);
    } catch {
      /* one listener failing must not affect checkout */
    }
  });
}

let loading: Promise<PaddleGlobal | null> | null = null;
let retainCustomer: string | null = null;

export function loadPaddle(): Promise<PaddleGlobal | null> {
  if (typeof window === "undefined" || !paddleConfigured)
    return Promise.resolve(null);
  if (loading) return loading;
  loading = new Promise((resolve) => {
    const init = () => {
      const P = window.Paddle;
      if (!P) return resolve(null);
      if (PADDLE_CLIENT_TOKEN.startsWith("test_"))
        P.Environment?.set("sandbox");
      P.Initialize({
        token: PADDLE_CLIENT_TOKEN,
        eventCallback: tell,
        ...(retainCustomer ? { pwCustomer: { id: retainCustomer } } : {}),
      });
      resolve(P);
    };
    if (window.Paddle) return init();
    const script = document.createElement("script");
    script.src = "https://cdn.paddle.com/paddle/v2/paddle.js";
    script.async = true;
    script.onload = init;
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return loading;
}

/** Paddle Retain: identify the signed-in customer by their Paddle customer ID
 *  (ctm_...), never an internal ID or email. */
export function setRetainCustomer(customerId: string | null | undefined) {
  if (
    !customerId ||
    !customerId.startsWith("ctm_") ||
    retainCustomer === customerId
  )
    return;
  retainCustomer = customerId;
  if (loading)
    loading.then((P) => P?.Update?.({ pwCustomer: { id: customerId } })).catch(() => undefined);
}

/** Money in the lowest unit ("2200") formatted for a currency ("$22", "£18.50", "¥2,200"). */
function formatMinor(minor: number, currency: string): string {
  try {
    const f = new Intl.NumberFormat("en", { style: "currency", currency, currencyDisplay: "narrowSymbol" });
    const digits = f.resolvedOptions().maximumFractionDigits ?? 2;
    const amount = minor / 10 ** digits;
    const whole = Number.isInteger(amount);
    return new Intl.NumberFormat("en", {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: whole ? 0 : digits,
      maximumFractionDigits: digits,
    }).format(amount);
  } catch {
    return `${currency} ${(minor / 100).toFixed(2)}`;
  }
}

/** The launch offer's prices from Paddle, in the visitor's currency: for each discount, the prices it covers,
 *  before (`was`) and after (`now`) the discount, tax not included (like the normal card price). Keyed by price ID. */
export async function previewDiscounted(
  groups: { discountId: string; priceIds: string[] }[],
): Promise<Record<string, { was: string; now: string }>> {
  const P = await loadPaddle();
  if (!P?.PricePreview || !groups.length) return {};
  const out: Record<string, { was: string; now: string }> = {};
  await Promise.all(
    groups.map(async ({ discountId, priceIds }) => {
      const ids = priceIds.filter(Boolean);
      if (!ids.length) return;
      try {
        const res = await P.PricePreview!({ items: ids.map((priceId) => ({ priceId, quantity: 1 })), discountId });
        const currency = res?.data?.currencyCode;
        if (!currency) return;
        for (const line of res?.data?.details?.lineItems ?? []) {
          const id = line.price?.id;
          const sub = Number(line.totals?.subtotal);
          const off = Number(line.totals?.discount ?? 0);
          if (!id || !Number.isFinite(sub) || !Number.isFinite(off) || off <= 0) continue;
          out[id] = { was: formatMinor(sub, currency), now: formatMinor(sub - off, currency) };
        }
      } catch {
        // our own numbers are shown instead
      }
    }),
  );
  return out;
}

/** Localized prices straight from Paddle (formattedTotals.subtotal), keyed by
 *  Paddle price ID. Paddle detects the visitor's country from their IP. */
export async function previewPrices(
  priceIds: string[],
): Promise<Record<string, string>> {
  const ids = priceIds.filter(Boolean);
  if (!ids.length) return {};
  const P = await loadPaddle();
  if (!P?.PricePreview) return {};
  try {
    const res = await P.PricePreview({
      items: ids.map((priceId) => ({ priceId, quantity: 1 })),
    });
    const out: Record<string, string> = {};
    for (const line of res?.data?.details?.lineItems ?? []) {
      const id = line.price?.id;
      const text =
        line.formattedTotals?.subtotal ?? line.formattedTotals?.total;
      if (id && text) out[id] = text;
    }
    return out;
  } catch {
    return {};
  }
}
